import { api } from '@packages/backend/convex/_generated/api';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { FunctionReturnType } from 'convex/server';
import { useQuery } from 'convex-helpers/react';
import { format, formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { ActionButton } from '@/components/ActionButton';
import { ActionDropdown } from '@/components/ActionDropdown';
import { ColorBadge } from '@/components/ColorBadge';
import { CopyTextButton } from '@/components/CopyTextButton';
import { DropdownMenuItemDestructive } from '@/components/DropdownMenuItemDestructive';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { IPadCommandsSubmenu } from '@/features/ipads/IPadCommandsSubmenu';
import { IPadStatus } from '@/features/ipads/IPadStatus';
import { RenameIPadDialog } from '@/features/ipads/RenameIPadDialog';
import { UnlinkIPadDialog } from '@/features/ipads/UnlinkIPadDialog';
import { usePosConfig } from '@/routes/restaurant/useRestaurantData';
import { AddIPadDialog } from './AddIPadDialog';
import { CashDrawerSettings } from './CashDrawerSettings';
import { formatDeviceCode } from './formatDeviceCode';

export const Route = createFileRoute('/r/$restaurantId/s/ipads/')({
  component: IPadListPage,
});

type IPad = FunctionReturnType<typeof api.iPads.listDevices>['devices'][number];

function IPadListPage() {
  const { restaurantId } = Route.useParams();
  const { data, isPending } = useQuery(api.iPads.listDevices, { restaurantId });
  const posConfig = usePosConfig();
  const [isAdding, setIsAdding] = useState(false);
  const [iPadToRename, setIPadToRename] = useState<IPad | null>(null);
  const [iPadToUnlink, setIPadToUnlink] = useState<IPad | null>(null);

  if (isPending) return <LoadingIcon />;

  const iPads = data?.devices ?? [];
  const deviceCodes = data?.deviceCodes ?? [];

  // The USB printer plugged into an iPad points at it via `attachedToIPadUuid`.
  const attachedPrinterName = (iPadUuid: string) =>
    posConfig?.posPrinters.find((p) => p.attachedToIPadUuid === iPadUuid)?.name ?? '-';

  return (
    <>
      {isAdding && <AddIPadDialog onClose={() => setIsAdding(false)} />}
      {iPadToRename && (
        <RenameIPadDialog
          iPad={iPadToRename}
          onClose={() => setIPadToRename(null)}
          restaurantId={restaurantId}
        />
      )}
      {iPadToUnlink && (
        <UnlinkIPadDialog
          iPad={iPadToUnlink}
          onClose={() => setIPadToUnlink(null)}
          restaurantId={restaurantId}
        />
      )}

      {deviceCodes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Active device codes</CardTitle>
            <CardDescription>
              Read these out to staff to sign in to the “OrderHub POS” iPad app. Each code works
              once.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deviceCodes.map((dc) => (
                  <TableRow key={dc._id}>
                    <TableCell className="font-mono font-medium tracking-widest">
                      <span className="inline-flex items-center gap-1">
                        {formatDeviceCode(dc.code)}
                        <CopyTextButton text={dc.code} />
                      </span>
                    </TableCell>
                    <TableCell>{dc.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>iPads</CardTitle>
          <CardDescription>
            iPads linked to this venue. Generate a code to add one, or unlink an iPad to sign it
            out.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {iPads.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Printer</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {iPads.map((iPad) => {
                  return (
                    <TableRow key={iPad._id}>
                      <TableCell>
                        <IPadStatus heartbeats={iPad.heartbeats} />
                      </TableCell>
                      <TableCell className="font-medium">
                        <span className="inline-flex items-center gap-2">
                          {iPad.name ?? 'Unnamed iPad'}
                          {iPad.uuid === posConfig?.primaryIPadUuid && (
                            <ColorBadge color="blue">Primary</ColorBadge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>{attachedPrinterName(iPad.uuid)}</TableCell>
                      <TableCell className="font-medium">
                        {iPad.nativeVersion == null || iPad.jsVersion == null
                          ? '-'
                          : `${iPad.nativeVersion}.${iPad.jsVersion}`}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {formatDistanceToNow(iPad.updatedAt, { addSuffix: true })}
                        </div>
                        <div className="text-muted-foreground">
                          {format(iPad.updatedAt, 'h:mm a d-MMM-yy')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <ActionDropdown>
                          <IPadCommandsSubmenu
                            restaurantId={restaurantId}
                            posTerminalUuid={iPad.uuid}
                          />

                          <DropdownMenuItem asChild>
                            <Link
                              to="/r/$restaurantId/s/ipads/$deviceId"
                              params={{ restaurantId, deviceId: iPad._id }}
                            >
                              View
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => setIPadToRename(iPad)}>
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItemDestructive onSelect={() => setIPadToUnlink(iPad)}>
                            Unlink iPad
                          </DropdownMenuItemDestructive>
                        </ActionDropdown>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          <ActionButton className="mt-4" onClick={() => setIsAdding(true)} variant="outline">
            Add iPad
          </ActionButton>
        </CardContent>
      </Card>

      {posConfig && (
        <CashDrawerSettings openCashDrawerTriggers={posConfig.openCashDrawerTriggers} />
      )}
    </>
  );
}
